import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

import { IrtRows } from "./Irt";
import type { InflightCard, IrtVerdict, StockRow } from "./api";
import { fmtDays, fmtMin, fmtRate, fmtUnits, inflightLabel, inflightSentence, minSource, num, parisDateTime, sourceLabel, storeLabel, velocityWindow } from "./format";

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right text-ks-ink">{value}</dd>
    </div>
  );
}

/**
 * The row detail opens in a shadcn Sheet (the Precision Pricing RowDetailSheet
 * pattern, same Margin Engine area), so the nine-column table keeps the full
 * width at 1280 px (S2, review B1).
 */
export function StockDetail({ row, cards = [], irt, onClose }: { row: StockRow | null; cards?: InflightCard[]; irt?: IrtVerdict; onClose: () => void }) {
  return (
    <Sheet open={row !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      {row ? (
        <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-[560px]" onCloseAutoFocus={(event) => event.preventDefault()}>
          <SheetHeader>
            <SheetTitle>{row.sku} · {storeLabel(row)}</SheetTitle>
            <SheetDescription>{row.name}{row.asin ? ` · ${row.asin}` : ""} · {row.row_kind}</SheetDescription>
          </SheetHeader>
          <Detail row={row} cards={cards} irt={irt} />
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

function Detail({ row, cards, irt }: { row: StockRow; cards: InflightCard[]; irt?: IrtVerdict }) {
  const w = velocityWindow(row);
  const min = fmtMin(row.effective_min_days);
  const inflight = inflightLabel(row);
  return (
    <div className="px-4 pb-4">
        <dl>
          <Row label="Units" value={`${fmtUnits(row.units)} (${sourceLabel(row)})`} />
          <Row label="Read at" value={parisDateTime(row.read_at) ?? "no reading"} />
          <Row label="Sales/day, 7 days" value={`${fmtRate(row.velocity_7d)}${w === "7d" ? " · used" : ""}`} />
          <Row label="Sales/day, 30 days" value={`${fmtRate(row.velocity_30d)}${w === "30d" ? " · used" : ""}`} />
          <Row label="Velocity source" value={row.velocity_stale ? "carried from the previous run" : "this run"} />
          <Row label="Minimum days" value={min == null ? "No minimum" : `${min} (${minSource(row)})`} />
          <Row label="Inbound units" value={fmtUnits(row.inbound_units) === "Unknown" ? "none" : fmtUnits(row.inbound_units)} />
          <Row label="Convertible units" value={num(row.convertible_units) ? `${fmtUnits(row.convertible_units)} (+${fmtDays(row.days_added) ?? 0} days)` : "none"} />
          <Row label="In flight" value={inflight === "" ? "none" : inflight} />
          <Row label="Outage date" value={row.outage_date ?? "none projected"} />
          <Row label="Final outage date" value={row.final_outage_date ?? "none projected"} />
        </dl>
        {cards.length ? (
          <div className="mt-3">
            <p className="text-sm font-medium text-ks-ink">Conversions in flight</p>
            <ul className="mt-1 flex flex-col gap-1 text-sm">
              {cards.map((card) => (
                <li key={card.page_id} className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{inflightSentence(card)}</span>
                  <a href={card.url} target="_blank" rel="noreferrer" className="shrink-0 text-ks-ink underline-offset-2 hover:underline">open card</a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <IrtRows verdict={irt} />
    </div>
  );
}
